import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Moon, Sun, Check } from "lucide-react";
import { useTheme } from "@/context/ThemeContext";

const Preview = ({ variant }) => {
  const dark = variant === "dark";
  return (
    <div
      className="mt-6 rounded-xl overflow-hidden border"
      style={{
        borderColor: dark ? "rgba(242,236,228,0.12)" : "rgba(38,35,46,0.14)",
        background: dark ? "#16151F" : "#F5F1E9",
      }}
    >
      <div className="p-5">
        <div
          className="font-serif-display text-lg font-light"
          style={{ color: dark ? "#F2ECE4" : "#26232E" }}
        >
          You are looking at yourself.
        </div>
        <div
          className="mt-2 text-xs"
          style={{ color: dark ? "#6C6C80" : "#8A8794" }}
        >
          A conversation with yourself.
        </div>
        <div
          className="mt-4 inline-block rounded-full px-4 py-1 text-[10px] tracking-[0.2em] uppercase border"
          style={{
            borderColor: dark ? "rgba(242,236,228,0.2)" : "rgba(38,35,46,0.25)",
            color: dark ? "#B79A7E" : "#8C7A6B",
          }}
        >
          Look
        </div>
      </div>
    </div>
  );
};

export default function ThemeSelect() {
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const [choice, setChoice] = useState(theme || "dark");

  const pick = (t) => {
    setChoice(t);
    setTheme(t);
  };

  const proceed = () => {
    setTheme(choice);
    navigate("/onboarding", { replace: true });
  };

  const cards = [
    { key: "dark", label: "Dark", icon: Moon, sub: "Quiet, low-light contemplation" },
    { key: "light", label: "Light", icon: Sun, sub: "Bright, open, and airy" },
  ];

  return (
    <div
      data-testid="theme-select-screen"
      className="min-h-screen flex flex-col items-center justify-center px-6 sm:px-10 py-16"
    >
      <motion.h1
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1 }}
        className="font-serif-display font-light text-3xl sm:text-4xl lg:text-5xl tracking-tight text-center"
      >
        How shall we look?
      </motion.h1>
      <p className="mt-4 text-center text-[var(--mirror-text-3)] text-sm max-w-md">
        Choose the light you'd like to reflect in. You can change this anytime in Settings.
      </p>

      <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 gap-6 w-full max-w-3xl">
        {cards.map(({ key, label, icon: Icon, sub }, i) => {
          const active = choice === key;
          return (
            <motion.button
              key={key}
              data-testid={`theme-card-${key}`}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15 * i }}
              onClick={() => pick(key)}
              className={`text-left rounded-2xl border p-6 transition-all duration-500 ${
                active
                  ? "border-[var(--mirror-accent)] ring-1 ring-[var(--mirror-accent)]"
                  : "border-[var(--mirror-border)] hover:border-[var(--mirror-border-strong)]"
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 text-[var(--mirror-accent)]">
                  <Icon size={18} />
                  <span className="text-sm tracking-[0.2em] uppercase text-[var(--mirror-text)]">
                    {label}
                  </span>
                </div>
                {active && <Check size={18} className="text-[var(--mirror-accent)]" />}
              </div>
              <p className="mt-2 text-xs text-[var(--mirror-text-3)]">{sub}</p>
              <Preview variant={key} />
            </motion.button>
          );
        })}
      </div>

      <motion.button
        data-testid="theme-continue-button"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 0.5 }}
        onClick={proceed}
        className="mt-14 px-10 py-3 rounded-full border border-[var(--mirror-border-strong)] text-sm tracking-[0.2em] uppercase text-[var(--mirror-text)] hover:bg-[var(--mirror-surface)] transition-colors duration-700"
      >
        Continue
      </motion.button>
    </div>
  );
}
